import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Localizer} from "@/main/react/Types";
import {ThoughtInConversationDTO} from "@/generated-client";
import ThoughtInConversationListDisplayer, {
    Props as ThoughtInConversationListDisplayerProps
} from "@/main/react/list/ThoughtInConversationListDisplayer";
import ThoughtTextEditor, {
    Props as ThoughtTextEditorProps
} from "@/main/react/editor/thought/ThoughtTextEditorLazy";
import {showThoughtTextEditorDialog} from "@/main/react/editor/thought/ThoughtTextEditorDialog";
import type {DialogController} from "@/main/react/Dialog/Dialog";
import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert";
import {CtaButton} from "@/main/react/buttons/CtaButton";
import {ArrowLeft, MessageSquarePlus} from "lucide-react";
import {useIsMobile} from "@/hooks/use-mobile";
import {INSERTED_ITEM_MOTION_HIGHLIGHT_DURATION_MS} from "@/main/react/component/InsertedItemMotion";

export type BaseProps = {
    localizer: Localizer;
} & ThoughtTextEditorProps & ThoughtInConversationListDisplayerProps;

enum DisplayMode {
    VIEWING = "VIEWING",
    PARTICIPANT_RESPONDING = "PARTICIPANT_RESPONDING",
    PARTICIPANT_WAITING = "PARTICIPANT_WAITING"
}

enum ParticipantSide {
    OPENING = "OPENING",
    REACTING = "REACTING"
}

function ConversationDisplayer(props: BaseProps & {
    items?: ThoughtInConversationDTO[]; // made optional — component will fetch if not provided
    shouldInferParticipantsFromArgumentOrder?: boolean;
    isSensitive?: boolean;
    suppressSpoilerGuards?: boolean;
    highlightedArgumentPublicId?: string;
    onBackToConversations?: () => void;
}) {

    const {
        conversationPublicId = "",
        loggedInUsername = "",
        agreementAgreementTitle = "Agree",
        disagreementAgreementTitle = "Disagree",
        isUserLoggedIn,
        localizer,
        items,
        // editor initial values
        openingArgumentPublicId,
        discussionPublicId,
        profileImageUrl,
        openingUsername,
        reactingUsername,
        shouldInferParticipantsFromArgumentOrder = false,
        isSensitive = false,
        suppressSpoilerGuards = false,
        highlightedArgumentPublicId,
        onBackToConversations
    } = props;

    const isMobile = useIsMobile();
    // On mobile the response editor opens as a dialog rather than mounting inline; keep its controller so
    // re-tapping "Reply" reuses the same mounted dialog and we can clean it up on unmount / turn change.
    const responseDialogRef = React.useRef<DialogController | null>(null);

    // Local copy of items so we can append new arguments locally and re-render
    const [localItems, setLocalItems] = useState<ThoughtInConversationDTO[]>(items ?? []);
    const [newlyPublishedArgumentPublicIds, setNewlyPublishedArgumentPublicIds] = useState<string[]>([]);
    const replyInsertionTimeoutsRef = React.useRef<Record<string, number>>({});

    const scheduleReplyInsertionHighlight = useCallback((argumentPublicId?: string | null) => {
        const normalizedArgumentPublicId = argumentPublicId?.trim();
        if (!normalizedArgumentPublicId) {
            return;
        }

        setNewlyPublishedArgumentPublicIds((prev) => prev.includes(normalizedArgumentPublicId)
            ? prev
            : [...prev, normalizedArgumentPublicId],
        );

        const existingTimeout = replyInsertionTimeoutsRef.current[normalizedArgumentPublicId];
        if (existingTimeout != null) {
            window.clearTimeout(existingTimeout);
        }

        replyInsertionTimeoutsRef.current[normalizedArgumentPublicId] = window.setTimeout(() => {
            setNewlyPublishedArgumentPublicIds((prev) => prev.filter((id) => id !== normalizedArgumentPublicId));
            delete replyInsertionTimeoutsRef.current[normalizedArgumentPublicId];
        }, INSERTED_ITEM_MOTION_HIGHLIGHT_DURATION_MS);
    }, []);

    useEffect(() => {
        setLocalItems(items ?? []);
        setNewlyPublishedArgumentPublicIds([]);
    }, [items]);

    useEffect(() => {
        return () => {
            for (const timeoutId of Object.values(replyInsertionTimeoutsRef.current)) {
                window.clearTimeout(timeoutId);
            }
            replyInsertionTimeoutsRef.current = {};
        };
    }, []);

    // NOTE: this component no longer self-fetches by conversationPublicId. The legacy flat "conversation"
    // read (GET /ajax/thoughts/conversation) was removed in the Discussion→threaded-replies migration
    // (commit 03ccde6a0), which deleted the Conversation entity and its public id — so conversationPublicId
    // no longer resolves to anything server-side and the old fetch was a dangling call that always
    // returned empty. Items are supplied by the parent; the threaded reply UI (subtreeUnderRootThought)
    // supersedes the flat conversation view. See the client-reconciliation note in
    // designs/04-live/2026-07-09-generated-client-reconciliation.md.

    const inferredOpeningUsername = useMemo(() => {
        return localItems.find(item => Boolean(item?.username))?.username?.trim() || undefined;
    }, [localItems]);

    const inferredReactingUsername = useMemo(() => {
        const normalizedOpeningUsername = inferredOpeningUsername?.trim();
        return localItems.find(item => {
            const username = item?.username?.trim();
            return Boolean(username) && username !== normalizedOpeningUsername;
        })?.username?.trim() || undefined;
    }, [inferredOpeningUsername, localItems]);

    const effectiveOpeningUsername = (openingUsername?.trim() || undefined) ?? inferredOpeningUsername;
    const effectiveReactingUsername = (reactingUsername?.trim() || undefined) ?? inferredReactingUsername;

    const loggedInParticipantSide = useMemo<ParticipantSide | null>(() => {
        if (!isUserLoggedIn || !loggedInUsername) {
            return null;
        }

        if (effectiveOpeningUsername && loggedInUsername === effectiveOpeningUsername) {
            return ParticipantSide.OPENING;
        }

        if (effectiveReactingUsername && loggedInUsername === effectiveReactingUsername) {
            return ParticipantSide.REACTING;
        }

        return null;
    }, [effectiveOpeningUsername, effectiveReactingUsername, isUserLoggedIn, loggedInUsername]);

    const lastArgumentSide = useMemo<ParticipantSide | null>(() => {
        if (localItems.length === 0) {
            return null;
        }

        return localItems.length % 2 === 1 ? ParticipantSide.OPENING : ParticipantSide.REACTING;
    }, [localItems.length]);

    const displayMode = useMemo<DisplayMode>(() => {
        if (!isUserLoggedIn || loggedInParticipantSide == null) {
            return DisplayMode.VIEWING;
        }

        if (lastArgumentSide == null) {
            return DisplayMode.PARTICIPANT_RESPONDING;
        }

        return lastArgumentSide === loggedInParticipantSide
            ? DisplayMode.PARTICIPANT_WAITING
            : DisplayMode.PARTICIPANT_RESPONDING;
    }, [isUserLoggedIn, loggedInParticipantSide, lastArgumentSide]);

    const handleThoughtPublished = useCallback((arg: ThoughtInConversationDTO) => {
        setLocalItems(prev => [...prev, arg]);
        scheduleReplyInsertionHighlight(arg?.publicId);
    }, [scheduleReplyInsertionHighlight, setLocalItems]);

    const openMobileResponseDialog = useCallback(() => {
        // Reuse the existing (kept-mounted) dialog if it's still mounted; otherwise open a fresh one.
        if (responseDialogRef.current) {
            responseDialogRef.current.open();
            return;
        }
        responseDialogRef.current = showThoughtTextEditorDialog({
            localizer,
            title: localizer("typescript.thought.reply"),
            loggedInUsername,
            isUserLoggedIn,
            profileImageUrl: profileImageUrl ?? undefined,
            openingArgumentPublicId: openingArgumentPublicId ?? undefined,
            conversationPublicId,
            discussionPublicId: discussionPublicId ?? undefined,
            agreementAgreementTitle,
            disagreementAgreementTitle,
            onThoughtPublished: (arg) => handleThoughtPublished(arg as ThoughtInConversationDTO),
        });
    }, [
        localizer,
        loggedInUsername,
        isUserLoggedIn,
        profileImageUrl,
        openingArgumentPublicId,
        conversationPublicId,
        discussionPublicId,
        agreementAgreementTitle,
        disagreementAgreementTitle,
        handleThoughtPublished,
    ]);

    // Tear down the response dialog when this conversation unmounts (navigation / tree refresh).
    useEffect(() => {
        return () => {
            try {
                responseDialogRef.current?.cleanup();
            } catch {
                // ignore teardown errors
            }
            responseDialogRef.current = null;
        };
    }, []);

    const backToConversationsLabel = localizer("discussion.back_to_conversations");

    return (
        <div className="mb-20 w-full min-w-0 max-w-200">
            {onBackToConversations ? (
                <div className="mb-3 flex items-center">
                    <CtaButton
                        icon={ArrowLeft}
                        text={backToConversationsLabel}
                        variant={"outline"}
                        size="sm"
                        requiresLogin={false}
                        onClick={() => onBackToConversations()}
                        extraClass="text-neutral-text hover:text-text-main"
                        playwrightId="conversation-back-to-thoughts"
                    />
                </div>
            ) : null}

            <ThoughtInConversationListDisplayer
                conversationPublicId={conversationPublicId}
                loggedInUsername={loggedInUsername}
                argumentsInConversation={localItems}
                agreementAgreementTitle={agreementAgreementTitle}
                disagreementAgreementTitle={disagreementAgreementTitle}
                isUserLoggedIn={isUserLoggedIn}
                localizer={localizer}
                openingUsername={effectiveOpeningUsername}
                reactingUsername={effectiveReactingUsername}
                isSensitive={isSensitive}
                suppressSpoilerGuards={suppressSpoilerGuards}
                highlightedArgumentPublicId={highlightedArgumentPublicId}
                shouldInferParticipantsFromArgumentOrder={shouldInferParticipantsFromArgumentOrder}
                newlyPublishedArgumentPublicIds={newlyPublishedArgumentPublicIds}
            />

            {displayMode === DisplayMode.PARTICIPANT_WAITING ? (
                <Alert className="mb-4 mt-4" role="status" aria-live="polite">
                    <AlertTitle>
                        {localizer("conversation.waitingForOtherTitle")}
                    </AlertTitle>
                    <AlertDescription>
                        {localizer("conversation.waitingForOther")}
                    </AlertDescription>
                </Alert>
            ) : null}

            {displayMode === DisplayMode.PARTICIPANT_RESPONDING ? (
                isMobile ? (
                    <div className="mb-4">
                        <CtaButton
                            icon={MessageSquarePlus}
                            text={localizer("typescript.thought.reply")}
                            variant={"primary"}
                            size="sm"
                            requiresLogin={false}
                            onClick={openMobileResponseDialog}
                            playwrightId="conversation-respond-open-dialog"
                        />
                    </div>
                ) : (
                    <div className="mb-4">
                        <ThoughtTextEditor
                            loggedInUsername={loggedInUsername}
                            isUserLoggedIn={isUserLoggedIn}
                            profileImageUrl={profileImageUrl ?? undefined}
                            localizer={localizer}
                            openingArgumentPublicId={openingArgumentPublicId ?? undefined}
                            conversationPublicId={conversationPublicId}
                            discussionPublicId={discussionPublicId ?? undefined}
                            agreementAgreementTitle={agreementAgreementTitle}
                            disagreementAgreementTitle={disagreementAgreementTitle}
                            onThoughtPublished={handleThoughtPublished}
                            hideAvatar={true}
                        />
                    </div>
                )
            ) : null}

        </div>
    );
}


export default ConversationDisplayer;
